// src/api/authService.js
// Centralized authentication service with refresh token support

import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from "../config";

class AuthService {
  /**
   * Login with email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise<Object>} Response data with access_token, refresh_token, user
   */
  async login(email, password) {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `Login failed (${response.status})`);
    }

    const data = await response.json();
    this.setTokens(data);
    return data;
  }

  /**
   * Store tokens and user data in localStorage
   * @param {Object} data - Response data containing access_token, refresh_token, user
   */
  setTokens(data) {
    if (data.access_token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, data.access_token);
    }
    if (data.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
    }
    if (data.user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    }
  }

  /**
   * Refresh the access token using the stored refresh token
   * @returns {Promise<string>} New access token
   * @throws {Error} If refresh fails
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REFRESH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      // Clear auth data on refresh failure
      this.logout();
      throw new Error("Token refresh failed");
    }

    const data = await response.json();

    // Update tokens
    if (data.access_token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, data.access_token);
    }
    if (data.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
    }

    return data.access_token;
  }

  /**
   * Logout - clear all authentication data
   */
  logout() {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  /**
   * Get the current access token
   * @returns {string|null} Access token or null
   */
  getToken() {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Get the current refresh token
   * @returns {string|null} Refresh token or null
   */
  getRefreshToken() {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get the current user data
   * @returns {Object|null} User object or null
   */
  getUser() {
    const userData = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated with a valid (non-expired) token
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    // Verify token is not expired
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds

      // Check if token expires within the next minute
      return Date.now() < exp - 60000;
    } catch {
      return false;
    }
  }

  /**
   * Get authorization headers for API requests
   * @returns {Object} Headers object with Authorization if token exists
   */
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Register a new user with an invite code
   * @param {Object} params - Registration data
   * @returns {Promise<Object>} Response data with access_token, user
   */
  async register({ email, password, name, invite_code, org_name, accept_terms, terms_version }) {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REGISTER}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name, invite_code, org_name, accept_terms, terms_version }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.detail || `Registration failed (${response.status})`);
      err.status = response.status;
      err.detail = error.detail || `Registration failed (${response.status})`;
      throw err;
    }

    const data = await response.json();
    this.setTokens(data);
    return data;
  }

  /**
   * Public self-service signup (auto-approved, no invite code)
   *
   * Resolves to either a created account or a waitlist placement, so callers
   * must branch on `status` rather than assuming tokens are present.
   *
   * @param {Object} params
   * @returns {Promise<Object>} { status: "registered"|"waitlisted", message, ... }
   */
  async signup({ email, password, name, organization_name, city, state, accept_terms, terms_version }) {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SIGNUP}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        name,
        organization_name,
        city,
        state,
        accept_terms,
        terms_version,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      // A Pydantic 422 returns detail as a list of issues, not a string
      const detail = Array.isArray(error.detail)
        ? error.detail[0]?.msg
        : error.detail;
      const message = detail || `Signup failed (${response.status})`;
      const err = new Error(message);
      err.status = response.status;
      err.detail = message;
      throw err;
    }

    const data = await response.json();
    // Waitlisted signups carry no tokens: there is no account yet.
    if (data.status === "registered") {
      this.setTokens(data);
    }
    return data;
  }

  /**
   * Verify the current token
   * @returns {Promise<Object>} { valid, user, expires_at }
   */
  async verify() {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.VERIFY}`, {
      method: "GET",
      headers: { ...this.getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error("Token verification failed");
    }

    return response.json();
  }

  /**
   * Change the password of the authenticated user
   *
   * Uses fetch directly instead of apiFetch: http.js imports authService, so
   * calling apiFetch from here would create a circular import.
   *
   * @param {Object} params
   * @param {string} params.currentPassword
   * @param {string} params.newPassword
   * @param {string} params.confirmPassword
   * @returns {Promise<Object>} Fresh access_token, refresh_token and user
   */
  async changePassword({ currentPassword, newPassword, confirmPassword }) {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.CHANGE_PASSWORD}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      // A Pydantic 422 returns detail as a list of issues, not a string
      const detail = Array.isArray(error.detail)
        ? error.detail[0]?.msg
        : error.detail;
      throw new Error(detail || `Password change failed (${response.status})`);
    }

    const data = await response.json();
    // The backend revoked every refresh token and issued a new pair, so store
    // it to keep this device signed in.
    this.setTokens(data);
    return data;
  }

  /**
   * Get trial status for the current user
   * @returns {Promise<Object>} Trial info
   */
  async getTrialStatus() {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.TRIAL_STATUS}`, {
      method: "GET",
      headers: { ...this.getAuthHeaders() },
    });

    if (!response.ok) {
      throw new Error("Failed to get trial status");
    }

    return response.json();
  }
}

// Export singleton instance
export const authService = new AuthService();
